import React, { useState, useEffect } from "react";
import { Typography, CircularProgress, Grid } from "@mui/material";
import axios from "axios";
import { StyledButton, StyledTypography } from "../styles/styles";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import Plot from "react-plotly.js";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const ValidateDigitalTwin = () => {
    const { selectedConfig, setSelectedConfig } = useBatteryConfig();

    const [plotData, setPlotData] = useState({
        fig_json: null,
        df_optimized_params: null,
        module_plots: null,
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const handleNavigateToSubmit = () => {
        navigate("/batteryscope/submit");
    };

    const handleNavigateToGenerateEIS = () => {
        navigate("/batteryscope/generate-eis");
    };

    useEffect(() => {
        if (!selectedConfig) return;

        const fetchPlotData = async () => {
            if (!selectedConfig.uploadData) {
                setError("No file selected");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append("file", selectedConfig.uploadData);

            try {
                const response = await axios.post(
                    `${process.env.BACKEND_URL}batteries/validate_digital_twin`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                const { fig_json, df_optimized_params, module_plots } = response.data;

                if (!fig_json || !df_optimized_params) {
                    throw new Error("Plot data is incomplete or incorrect.");
                }
                setPlotData({
                    fig_json,
                    df_optimized_params,
                    module_plots,
                });

                setSelectedConfig((prev) => ({
                    ...prev,
                    equivalent_circuit_params: df_optimized_params,
                }));

                setError("");
                setLoading(false);
            } catch (error) {
                setError(error.message || "Error fetching plot data. Please check the data and try again.");
                setLoading(false);
            }
        };

        fetchPlotData();
    }, [selectedConfig]);

    return (
        <div className="visualize-data" style={{ textAlign: "center", padding: 20 }}>
            {!selectedConfig ? (
                <div className="mt-4">
                    <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
                        No Battery Data Selected
                    </StyledTypography>
                    <Typography variant="body1" gutterBottom className="mt-2 mb-4">
                        To visualize battery data, please submit your battery configuration first.
                    </Typography>
                    <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                        Go to Submit Page
                    </StyledButton>
                </div>
            ) : loading ? (
                <CircularProgress />
            ) : (
                <>
                    {error ? (
                        <>
                            <Typography variant="body1" color="error" gutterBottom>
                                {error}
                            </Typography>
                            <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                                Go to Submit Page
                            </StyledButton>
                        </>
                    ) : (
                        <>
                            <div style={{ maxHeight: "78vh", overflowY: "auto", width: "100%" }}>
                                <div className="flex flex-col gap-3 w-full h-full">
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                        {plotData.fig_json && (
                                            <Plot
                                                data={plotData.fig_json.data}
                                                layout={{
                                                    ...plotData.fig_json.layout,
                                                    autosize: true,
                                                    showlegend: false,
                                                    annotations: [
                                                        {
                                                            x: 3,
                                                            y: 378.52,
                                                            xref: 'x',
                                                            yref: 'y',
                                                            text: 'Voltage of Uploaded Data',
                                                            showarrow: true,
                                                            arrowhead: 2,
                                                            ax: 0,
                                                            ay: -40,
                                                            font: {
                                                                color: 'white',
                                                            },
                                                        },
                                                        {
                                                            x: 3,
                                                            y: 372.14,
                                                            xref: 'x',
                                                            yref: 'y',
                                                            text: 'Digital Twin Voltage',
                                                            showarrow: true,
                                                            arrowhead: 2,
                                                            ax: 0,
                                                            ay: 40,
                                                            font: {
                                                                color: 'white',
                                                            },
                                                        },
                                                    ],
                                                }}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-row gap-3" style={{ width: "100%", overflowX: "auto" }}>
                                        {plotData.module_plots && plotData.module_plots.length > 0 ? (
                                            plotData.module_plots.map((plot, index) => (
                                                <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                                    <Plot
                                                        key={index}
                                                        data={plot.data}
                                                        layout={{
                                                            ...plot.layout,
                                                            autosize: true,
                                                            showlegend: false,
                                                            annotations: [
                                                                {
                                                                    x: 3,
                                                                    y: 378.52,
                                                                    xref: 'x',
                                                                    yref: 'y',
                                                                    text: 'Voltage of module ' + (index+1),
                                                                    showarrow: true,
                                                                    arrowhead: 2,
                                                                    ax: 0,
                                                                    ay: -40,
                                                                    font: {
                                                                        color: 'white',
                                                                    },
                                                                },
                                                                {
                                                                    x: 3,
                                                                    y: 372.14,
                                                                    xref: 'x',
                                                                    yref: 'y',
                                                                    text: 'Digital Twin Voltage',
                                                                    showarrow: true,
                                                                    arrowhead: 2,
                                                                    ax: 0,
                                                                    ay: 40,
                                                                    font: {
                                                                        color: 'white',
                                                                    },
                                                                },
                                                            ],
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <Typography color="error">Module data was not present in your CSV.</Typography>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end">
                                {/* <Typography
                                    variant="body1"
                                    gutterBottom
                                    className="mt-2 mr-2"
                                    sx={{ color: "#e8442d", fontFamily: "Bai Jamjuree" }}
                                >
                                    Click on the plots to zoom in/out.
                                </Typography> */}
                                <StyledButton
                                    variant="contained"
                                    onClick={handleNavigateToGenerateEIS}
                                    className="gap-2"
                                >
                                    Go to EIS Analysis <ArrowForwardIcon />
                                </StyledButton>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ValidateDigitalTwin;
